import React, { useEffect, useRef, useState } from 'react'

import type { SanityTextContentProps } from '@slite/components'

import { SectionLastCTA } from '../../../components/SectionLastCTA'
import { SlitePage } from '../../../components/SlitePage'
import { useRoutes } from '../../../routes'
import type { UseCase } from './+data'
import { SectionUseCaseContent } from './SectionUseCaseContent'
import { SectionUseCaseHero } from './SectionUseCaseHero'

// Navbar height — ignored by the observer so the bar turns solid the moment
// the hero scrolls underneath it.
const NAV_HEIGHT = 64

export function UseCaseDetail({ useCase }: { useCase: UseCase }) {
  const routes = useRoutes()
  const pageUrl = routes.solutions.detail({ slug: useCase.slug.current })

  // The navbar floats over the dark hero as a transparent/inverted overlay
  // while the hero is behind it, then turns solid once it scrolls past.
  const heroRef = useRef<HTMLDivElement>(null)
  const [isHeroBehindNav, setIsHeroBehindNav] = useState(true)

  useEffect(() => {
    const hero = heroRef.current
    if (!hero) {
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => setIsHeroBehindNav(entry?.isIntersecting ?? false),
      { rootMargin: `-${NAV_HEIGHT}px 0px 0px 0px`, threshold: 0 }
    )
    observer.observe(hero)
    return () => observer.disconnect()
  }, [])

  return (
    <SlitePage
      title={useCase.openGraphTitle ?? useCase.title}
      description={useCase.metaDescription ?? useCase.subtitle}
      ogImage={useCase.openGraphImage?.url ?? undefined}
      ogTitle={useCase.openGraphTitle ?? useCase.title}
      ogDescription={useCase.openGraphDescription ?? useCase.subtitle}
      canonicalUrl={pageUrl}
      background="moon-dust-lighter"
      transparentNavbar
      navbarInverted={isHeroBehindNav}
    >
      <SectionUseCaseHero
        heroRef={heroRef}
        title={useCase.title}
        description={useCase.subtitle}
        videoSnippet={useCase.heroVideo?.url ?? ''}
        videoLink={useCase.videoLink}
      />
      <SectionUseCaseContent
        content={useCase.content as SanityTextContentProps}
      />
      <SectionLastCTA background="moon-dust-lighter" />
    </SlitePage>
  )
}
